/**
 * Hilfsfunktionen für responsive Bilder mit den vom Script
 * scripts/generate-responsive-variants.mjs generierten Varianten.
 *
 * Konvention: Original liegt unter /images/foo.webp (oder .jpg/.png),
 * Varianten unter /images/foo-{480,800,1280,1920}w.webp (+ optional .avif).
 *
 * Nur für public/-Pfade (mit führendem "/"). Für src/assets/-Imports
 * funktioniert das nicht (Vite hashed die Dateinamen).
 */

const DEFAULT_WIDTHS = [480, 800, 1280, 1920] as const;
const VARIANT_RE = /-(480|800|1280|1920)w\.(webp|avif)$/;
// Vite-Bundled Assets (z. B. import x from "@/assets/...") landen in
// /assets/<name>-<hash>.<ext> oder im SSR-Dev unter /src/assets/<name>.<ext>.
// Für diese gibt es KEINE generierten Varianten — Helper darf hier kein srcSet bauen.
const VITE_BUNDLED_RE = /^\/(src\/)?assets\//;

function isPublicPath(url: string): boolean {
  if (!url || !url.startsWith("/")) return false;
  if (VARIANT_RE.test(url)) return false;
  if (VITE_BUNDLED_RE.test(url)) return false;
  return true;
}

function basename(url: string): string {
  const dotIdx = url.lastIndexOf(".");
  return dotIdx === -1 ? url : url.slice(0, dotIdx);
}

export function deriveMobileVariant(url: string): string | undefined {
  if (!isPublicPath(url)) return undefined;
  return `${basename(url)}-800w.webp`;
}

/**
 * Baut einen WebP-srcSet-String mit allen verfügbaren Breiten.
 */
export function buildSrcSet(
  url: string,
  originalWidth = 1920,
  widths: readonly number[] = DEFAULT_WIDTHS,
): string | undefined {
  if (!isPublicPath(url)) return undefined;
  const base = basename(url);
  const parts = widths
    .filter((w) => w <= originalWidth)
    .map((w) => `${base}-${w}w.webp ${w}w`);
  if (parts.length === 0) return undefined;
  return parts.join(", ");
}

/**
 * Baut ein AVIF-srcSet (Variante muss generiert sein).
 */
export function buildAvifSrcSet(
  url: string,
  originalWidth = 1920,
  widths: readonly number[] = DEFAULT_WIDTHS,
): string | undefined {
  if (!isPublicPath(url)) return undefined;
  const base = basename(url);
  const parts = widths
    .filter((w) => w <= originalWidth)
    .map((w) => `${base}-${w}w.avif ${w}w`);
  if (parts.length === 0) return undefined;
  return parts.join(", ");
}

/**
 * Pfad zu einer spezifischen Variante (für `src`-Fallback im img).
 */
export function variantSrc(url: string, width: number, fmt: "webp" | "avif" = "webp"): string | undefined {
  if (!isPublicPath(url)) return undefined;
  return `${basename(url)}-${width}w.${fmt}`;
}
